import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Briefcase, Plus } from "lucide-react";
import { PortalScreen } from "@/components/portal/lp/PortalScreen";
import WorkWeekEditor from "@/components/portal/lp/WorkWeekEditor";
import { lpApi, lpErr, when, card, h2, input, btn, ghost } from "@/components/portal/lp/lpApi";

const TYPES = [["appointment", "Appointment"], ["court", "Court"], ["work", "Work shift"], ["class", "Class"], ["church", "Church / spiritual"], ["volunteer", "Volunteer"], ["fitness", "Fitness"], ["personal", "Personal"]];
const toLocal = (iso) => { const d = new Date(iso); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 16); };

function EventForm({ api, rid, ev, onDone, onCancel }) {
  const now = new Date(); now.setMinutes(0, 0, 0); now.setHours(now.getHours() + 1);
  const [f, setF] = useState(() => ev?.id ? { title: ev.title, event_type: "personal", location: ev.location || "", start: toLocal(ev.start_time), end: toLocal(ev.end_time), notes: ev.notes || "" }
    : { title: ev?.preset === "work" ? "Work shift" : "", event_type: ev?.preset === "work" ? "work" : "appointment", location: "", start: toLocal(now.toISOString()), end: toLocal(new Date(now.getTime() + 3600000).toISOString()), notes: "" });
  const [clash, setClash] = useState(null);
  const save = async (e) => {
    e.preventDefault(); setClash(null);
    const body = { title: f.title, location: f.location, notes: f.notes, start_time: new Date(f.start).toISOString(), end_time: new Date(f.end).toISOString() };
    try {
      if (ev?.id) await api.patch(`/residents/${rid}/calendar-events/${ev.id}`, body);
      else await api.post(`/residents/${rid}/calendar-events`, { ...body, event_type: f.event_type });
      toast.success(ev?.id ? "Event updated" : "Added to your schedule"); onDone();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d?.code === "conflict") setClash(d); else toast.error(lpErr(err));
    }
  };
  return (
    <form onSubmit={save} className={`${card} space-y-2`} data-testid="cal-form">
      <input className={input} required placeholder="What is it?" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="cal-form-title" />
      {!ev?.id && <select className={input} value={f.event_type} onChange={(e) => setF({ ...f, event_type: e.target.value })} data-testid="cal-form-type">{TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="text-xs text-white/50">Starts<input type="datetime-local" className={input} required value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} data-testid="cal-form-start" /></label>
        <label className="text-xs text-white/50">Ends<input type="datetime-local" className={input} required value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} data-testid="cal-form-end" /></label>
      </div>
      <input className={input} placeholder="Where (optional)" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="cal-form-location" />
      {clash && (
        <div data-testid="cal-form-clash" className="rounded-md border border-red-400/40 bg-red-400/10 p-3 text-sm text-red-100">
          <b>That time clashes.</b>
          <ul className="mt-1 list-disc pl-5 text-xs">{(clash.conflicts?.length ? clash.conflicts.map((c) => (c.message || c.title || "").replace(/^.+? is already booked for /, "You already have ")) : [clash.message]).map((m, i) => <li key={i}>{m}</li>)}</ul>
          Pick another time.
        </div>
      )}
      <div className="flex gap-2"><button className={btn} data-testid="cal-form-save">{ev?.id ? "Save" : "Add to my schedule"}</button><button type="button" className={ghost} onClick={onCancel} data-testid="cal-form-cancel">Cancel</button></div>
    </form>
  );
}

function WorkWeek({ api, stamp, onOpen }) {
  const [w, setW] = useState(null);
  useEffect(() => { api.get("/portal/work-schedule").then(setW).catch(() => setW(null)); }, [stamp]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!w) return null;
  return (
    <button type="button" onClick={onOpen} className={`${card} w-full text-left space-y-2 hover:border-[#d4a017]/60 transition-colors`} data-testid="cal-work-week">
      <div className="flex items-baseline justify-between"><h2 className={h2}>My work week</h2><span className="text-sm text-white/60">{w.total_hours} hrs · <span className="text-[#d4a017]">Edit</span></span></div>
      <div className="grid grid-cols-7 gap-1 text-center text-[11px]">
        {w.days.map((d) => <div key={d.date} className={`rounded p-1.5 ${d.shifts.length ? "bg-[#d4a017]/15 text-[#f0d27a]" : "bg-white/[0.03] text-white/40"}`}><div>{d.day.slice(0, 3)}</div><div className="font-semibold">{d.hours || "—"}</div></div>)}
      </div>
    </button>
  );
}

export default function CalendarTab({ token, rid }) {
  const api = lpApi(token);
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null);
  const load = useCallback(() => api.get(`/residents/${rid}/calendar-events`, { days_back: 0, days_ahead: 60 }).then((d) => setRows(d.events)).catch((e) => toast.error(lpErr(e))), [token, rid]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  const remove = async (ev) => {
    if (!window.confirm(`Remove "${ev.title}" from your calendar?`)) return;
    try { await api.del(`/residents/${rid}/calendar-events/${ev.id}`); toast.success("Removed"); load(); } catch (e) { toast.error(lpErr(e)); }
  };
  const byDay = (rows || []).reduce((m, e) => { const k = new Date(e.start_time).toDateString(); (m[k] = m[k] || []).push(e); return m; }, {});
  return (
    <div className="space-y-4" data-testid="tab-calendar">
      <WorkWeek api={api} stamp={rows} onOpen={() => setForm({ work: true })} />
      <div className="grid grid-cols-2 gap-2">
        <button type="button" className={btn} onClick={() => setForm({ work: true })} data-testid="cal-add-work"><Briefcase className="h-4 w-4" /> Set my work week</button>
        <button type="button" className={btn} onClick={() => setForm({})} data-testid="cal-add"><Plus className="h-4 w-4" /> Add an event</button>
      </div>
      {form?.work && (
        <PortalScreen title="My work week" onBack={() => setForm(null)} testid="cal-screen-work">
          <WorkWeekEditor api={api} rid={rid} onSaved={() => { setForm(null); load(); }} />
        </PortalScreen>
      )}
      {form && !form.work && (
        <PortalScreen title={form.ev?.id ? "Edit event" : form.ev?.preset === "work" ? "Add work shift" : "Add an event"} onBack={() => setForm(null)} testid="cal-screen">
          <EventForm api={api} rid={rid} ev={form.ev} onCancel={() => setForm(null)} onDone={() => { setForm(null); load(); }} />
        </PortalScreen>
      )}
      {!rows ? <p className="text-white/50 text-sm">Loading…</p> : !rows.length ? <p data-testid="cal-empty" className="text-white/50 text-sm">Nothing on your calendar for the next 60 days.</p> : (
        Object.entries(byDay).map(([k, list]) => (
          <section key={k} className="space-y-1.5">
            <h3 className="text-[11px] uppercase tracking-[0.2em] text-white/50">{new Date(list[0].start_time).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })}</h3>
            {list.map((e) => (
              <div key={e.id} className={`${card} !p-3 flex items-start gap-3`} data-testid={`cal-event-${e.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{e.title}</div>
                  <div className="text-xs text-white/50">{when(e.start_time)}{e.location ? ` · ${e.location}` : ""}{e.created_by_resident ? " · added by you" : ""}</div>
                </div>
                {e.can_edit && (
                  <div className="flex gap-1 shrink-0">
                    <button type="button" className={ghost} onClick={() => setForm({ ev: e })} data-testid={`cal-event-edit-${e.id}`}>Edit</button>
                    <button type="button" className={ghost} onClick={() => remove(e)} data-testid={`cal-event-delete-${e.id}`}>Remove</button>
                  </div>
                )}
              </div>
            ))}
          </section>
        ))
      )}
    </div>
  );
}
